/*
 * In-page anchor navigation.
 *
 * Two things make native fragment links unreliable in this theme:
 *
 * 1. Shopify's navigation editor often stores menu items as absolute URLs
 *    (https://shop.myshopify.com/#technology). Clicking one is a real
 *    navigation rather than a same-document jump, so the page reloads at the
 *    top — and inside a theme preview it also drops ?preview_theme_id.
 * 2. At >=990px Horizon sets html/body to overflow:hidden and scrolls
 *    .page-wrapper instead, so the offset maths has to target that element.
 *
 * Any link that resolves to the current path and carries a fragment matching
 * an element on the page is handled here instead, with the sticky header
 * height subtracted so the section heading is not hidden underneath it.
 */

const HEADER_GAP = 24;

/** @returns {Element} The element that actually scrolls. */
function getScroller() {
  const wrapper = document.querySelector('.page-wrapper');
  if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1) return wrapper;
  return document.scrollingElement || document.documentElement;
}

/** @returns {number} Sticky header height plus breathing room. */
function getHeaderOffset() {
  const raw = getComputedStyle(document.body).getPropertyValue('--header-group-height');
  const height = Number.parseFloat(raw);
  return (Number.isFinite(height) ? height : 0) + HEADER_GAP;
}

/**
 * @param {string} hash
 * @returns {HTMLElement | null}
 */
function targetFor(hash) {
  if (!hash || hash === '#') return null;
  let id = hash.slice(1);
  try {
    id = decodeURIComponent(id);
  } catch {
    /* keep the raw value */
  }
  return document.getElementById(id);
}

/**
 * @param {HTMLElement} target
 * @param {boolean} smooth
 */
function scrollToTarget(target, smooth) {
  const scroller = getScroller();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = smooth && !reduced ? 'smooth' : 'auto';
  const isDocument = scroller === document.scrollingElement || scroller === document.documentElement;

  const top = isDocument
    ? target.getBoundingClientRect().top + window.scrollY - getHeaderOffset()
    : target.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop -
      getHeaderOffset();

  scroller.scrollTo({ top: Math.max(0, top), behavior });
}

/** Closes the mobile drawer so it cannot cover the section just jumped to. */
function closeHeaderMenus() {
  document
    .querySelectorAll('#header-group details[open]')
    .forEach((details) => {
      details.classList.remove('menu-open');
      details.removeAttribute('open');
    });
  document.querySelector('header-drawer')?.close?.();
}

/**
 * @param {HTMLElement} target
 */
function focusTarget(target) {
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
}

document.addEventListener('click', (event) => {
  if (event.defaultPrevented) return;
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const link = /** @type {HTMLElement} */ (event.target)?.closest?.('a[href]');
  if (!link || link.target === '_blank' || link.hasAttribute('download')) return;

  let url;
  try {
    url = new URL(/** @type {HTMLAnchorElement} */ (link).href, window.location.href);
  } catch {
    return;
  }

  if (url.origin !== window.location.origin) return;

  // Compare paths only: the query string differs inside a theme preview, and a
  // trailing slash should not count as a different page.
  const normalise = (path) => path.replace(/\/+$/, '') || '/';
  if (normalise(url.pathname) !== normalise(window.location.pathname)) return;

  const target = targetFor(url.hash);
  if (!target) return;

  event.preventDefault();
  closeHeaderMenus();
  // Keep the current query so a theme preview stays in preview.
  window.history.pushState(null, '', `${window.location.pathname}${window.location.search}${url.hash}`);
  scrollToTarget(target, true);
  focusTarget(target);
});

// A fragment arriving from another page needs the same header offset applied.
function settleInitialHash() {
  const target = targetFor(window.location.hash);
  if (target) requestAnimationFrame(() => scrollToTarget(target, false));
}

if (document.readyState === 'complete') settleInitialHash();
else window.addEventListener('load', settleInitialHash, { once: true });

window.addEventListener('hashchange', () => {
  const target = targetFor(window.location.hash);
  if (target) scrollToTarget(target, true);
});
